/**
 * Support system — in-house replacement for Intercom.
 *
 * Direction: a restaurant dashboard user talks to **Gastronaut support**
 * (internal support), AI-assisted via an ElevenLabs agent. This is NOT the
 * guest-facing Posteingang/Team-Chat (restaurant ↔ its guests) — keep them
 * separate.
 *
 * Firestore layout (all top-level, `support`-prefixed, with a denormalized
 * `restaurantId` so the dashboard can scope-read its own and the admin
 * dashboard / support-team queues can query across all restaurants):
 *
 *   supportConversations/{conversationId}
 *   supportConversations/{conversationId}/messages/{messageId}
 *   supportTickets/{ticketId}
 *   supportHelpArticles/{articleId}
 *   supportBroadcastMessages/{broadcastId}
 *
 * Writes go through the `/v03/support/*` API (Admin SDK); the frontends read
 * Firestore directly (realtime). Source of truth for these types is this file;
 * a vendored copy lives in `next-dashboard/src/types/shared/documents/support.ts`.
 */

import { Translation } from '../helper';

/** Audit fields shared by support documents (numeric ms timestamps). */
type WithAudit<T> = T & {
  createdAt: number;
  /** uid of the writer, or a sentinel: 'ai' | 'auto_response' | 'system'. */
  createdBy: string;
  updatedAt?: number;
  updatedBy?: string;
};

/* ────────────────────────────────────────────────────────────────────────
 * Shared primitives
 * ──────────────────────────────────────────────────────────────────────── */

/** Sentinel author ids used when no human uid is the writer. */
export const SUPPORT_AI_UID = 'ai';
export const SUPPORT_AUTO_RESPONSE_UID = 'auto_response';
export const SUPPORT_SYSTEM_UID = 'system';

/**
 * Gastronaut support teams = the "groups" a conversation can belong to or be
 * assigned to. Matches the AI tool `escalateToHuman(team)`.
 */
export type SupportTeam = 'support' | 'billing' | 'technical' | 'concierge';

/**
 * Who currently owns the conversation:
 *  - 'ai'          → the AI agent is handling it
 *  - a uid         → a specific human support agent
 *  - a SupportTeam → queued to a team
 *  - null          → unassigned
 */
export type SupportAssignee = 'ai' | SupportTeam | string;

/** Phase 1 is dashboard-only; the rest are reserved for phase 2+. */
export type SupportChannel = 'dashboard' | 'email' | 'whatsapp' | 'phone';

// "resolved" = handled by support but still open for the restaurant: the
// customer sees it as open and a reply reopens it. Mirror of the backend type.
export type SupportConversationStatus = 'open' | 'closed' | 'blocked' | 'resolved';

/**
 * Conversation severity, 0 (low) … 3 (critical). Numeric to stay close to the
 * agreed spec and to allow simple ordering in queues.
 */
export enum SupportSeverity {
  LOW = 0,
  NORMAL = 1,
  HIGH = 2,
  CRITICAL = 3,
}

/** Rating + free-text, used for both guest- and agent-side CSAT. */
export interface SupportFeedback {
  rating: number;
  message: string;
}

/** The dashboard user a conversation belongs to (the "local context"). */
export interface SupportUser {
  id: string;
  restaurant: { id: string; name: string };
  email: string;
  /** Not stored for dashboard users today; optional, backfill later. */
  phoneNumber?: string;
}

/**
 * Free-form local context captured at conversation start — mirrors what the
 * old Intercom messenger received (restaurant, role, products, language, the
 * screen the user was on, app version, etc.). Also passed to the AI agent.
 */
export interface SupportConversationMetadata {
  language?: string;
  role?: string;
  products?: string[];
  page?: string;
  appVersion?: string;
  userAgent?: string;
  [key: string]: unknown;
}

/** Denormalized preview of the latest message for conversation lists. */
export interface SupportLastMessage {
  id: string;
  preview: string;
  uid: string;
  type: SupportMessageType;
  createdAt: number;
  fromCustomer?: boolean;
}

/* ────────────────────────────────────────────────────────────────────────
 * Conversations
 * ──────────────────────────────────────────────────────────────────────── */

export type SupportConversationDocument = WithAudit<{
  id: string;
  /** Denormalized for security rules + cross-restaurant queries. */
  restaurantId: string;
  channel: SupportChannel;
  status: SupportConversationStatus;
  assignedTo: SupportAssignee | null;
  /** Owning support team (queue). */
  group: SupportTeam | null;
  severity: number;
  tags: string[];
  /** Short title — template label or AI-derived. */
  subject?: string;
  /** Which start-button/template opened this conversation. */
  templateId?: string;
  user: SupportUser;
  metadata?: SupportConversationMetadata;
  notifyByEmail: boolean;
  /** Address to notify the user at (defaults to user.email; user-editable). */
  notifyEmail?: string;
  /** Hard stop for the AI (set by an agent taking over). */
  stopAIFromReplying: boolean;
  /** Typing indicators: uid → last-typing timestamp. */
  writing: { [uid: string]: number };
  /** Linked supportTicket ids. */
  tickets: string[];
  /** Denormalized for list rendering without reading the subcollection. */
  lastMessage?: SupportLastMessage;
  /** Last read timestamp (ms) per uid, written by POST /read. The dashboard's
   *  unread signal: lastMessage.createdAt > readAt[uid]. */
  readAt?: { [uid: string]: number };
  userFeedback?: SupportFeedback;
  adminFeedback?: SupportFeedback;
  /** When the auto-close sweep should close an idle conversation. */
  automaticallyCloseAt?: number;
  closedAt?: number | null;
  resolvedAt?: number | null;
}>;

/* ────────────────────────────────────────────────────────────────────────
 * Messages (subcollection of a conversation)
 * ──────────────────────────────────────────────────────────────────────── */

export type SupportMessageType =
  | 'note' // internal agent note, not shown to the user
  | 'message' // normal chat message
  | 'multiple_choice' // a prompt offering selectable options
  | 'function_call'; // an AI tool call (with its result inline)

/** AI tool names exposed to the ElevenLabs agent. */
export type SupportToolName =
  | 'navigateToPage'
  | 'presentHelpArticle'
  | 'escalateToHuman'
  | 'searchTicketWithQuery'
  | 'getTicketById'
  | 'openTicket';

export type SupportFunctionCallStatus = 'pending' | 'success' | 'error';

export interface SupportTextBlock {
  type: 'text';
  text: string;
}

export interface SupportMultipleChoiceBlock {
  type: 'multiple_choice';
  /** Optional prompt shown above the options. */
  prompt?: string;
  options: { id: string; label: string; value?: string }[];
  /** Set once the user picks one. */
  selectedId?: string;
}

export interface SupportFunctionCallBlock {
  type: 'function_call';
  name: SupportToolName;
  args: Record<string, unknown>;
  status: SupportFunctionCallStatus;
  result?: unknown;
  error?: string;
}

export interface SupportArticleCardBlock {
  type: 'article_card';
  articleId: string;
  title?: string;
}

export interface SupportTicketCardBlock {
  type: 'ticket_card';
  ticketId: string;
  title?: string;
  status?: SupportTicketStatus;
}

export interface SupportEscalationBlock {
  type: 'escalation';
  team: SupportTeam;
  note?: string;
}

export interface SupportAttachmentBlock {
  type: 'attachment';
  url: string;
  name: string;
  mimeType: string;
  size?: number;
}

/** A message's content is an ordered list of typed blocks. */
export type SupportMessageBlock =
  | SupportTextBlock
  | SupportMultipleChoiceBlock
  | SupportFunctionCallBlock
  | SupportArticleCardBlock
  | SupportTicketCardBlock
  | SupportEscalationBlock
  | SupportAttachmentBlock;

export interface SupportThumb {
  dir: 'up' | 'down';
  ts: number;
  by: string;
}

export interface SupportMessageDocument {
  id: string;
  conversationId: string;
  /** Denormalized for subcollection security rules. */
  restaurantId: string;
  type: SupportMessageType;
  /** Author: a uid, or 'ai' | 'auto_response' | 'system'. */
  uid: string;
  draft: boolean;
  content: SupportMessageBlock[];
  thumb?: SupportThumb;
  createdAt: number;
  updatedAt?: number;
  /** Set when an admin edited the message text after sending. */
  editedAt?: number;
  editedBy?: string;
  /** Prior text versions, appended on every edit (audit trail). */
  editHistory?: { text: string; editedAt: number; editedBy: string }[];
  /** Soft-delete: hidden from the restaurant, restorable in the admin cockpit. */
  deleted?: boolean;
  deletedAt?: number;
  deletedBy?: string;
  /** True when the restaurant/guest authored it (vs. a support agent or the AI).
   *  Set from the auth path at write time; absent on pre-existing messages. */
  fromCustomer?: boolean;
}

/* ────────────────────────────────────────────────────────────────────────
 * Tickets (bug / feature / request / billing) — internal, no GitHub coupling
 * ──────────────────────────────────────────────────────────────────────── */

export type SupportTicketType = 'feature' | 'bug' | 'request' | 'billing';

export type SupportTicketStatus =
  | 'backlog'
  | 'open'
  | 'in_progress'
  | 'needs_review'
  | 'done'
  | 'closed';

export interface SupportTicketNote {
  uid: string;
  msg: string;
  ts: number;
}

export type SupportTicketDocument = WithAudit<{
  id: string;
  restaurantId: string;
  /** Conversation this ticket was opened from, if any. */
  conversationId?: string;
  type: SupportTicketType;
  title: string;
  description: string;
  status: SupportTicketStatus;
  messages: SupportTicketNote[];
  /** Points to another ticket if this is a duplicate. */
  duplicateOf?: string;
  /** Free-text reference (e.g. a GitHub issue URL), set manually by an agent. */
  connectedIssue?: string;
  severity?: number;
  tags?: string[];
  assignedTo?: SupportAssignee | null;
  /** Hidden from the restaurant's ticket list (internal/AI-monitoring tickets). */
  hideForCustomer?: boolean;
  /** When `status` last changed; `updatedAt` also moves on notes, tags and PR events. */
  statusChangedAt?: number;
}>;

/* ────────────────────────────────────────────────────────────────────────
 * Help articles (knowledge base)
 * ──────────────────────────────────────────────────────────────────────── */

export type SupportHelpArticleState = 'draft' | 'published';

export type SupportHelpArticleDocument = WithAudit<{
  id: string;
  slug: string;
  title: Translation<string>;
  /** HTML or Markdown body per language. */
  body: Translation<string>;
  excerpt?: Translation<string>;
  collection?: string;
  section?: string;
  tags?: string[];
  state: SupportHelpArticleState;
  order?: number;
  /** Products this article is relevant to (optional filtering). */
  products?: string[];
  viewCount?: number;
}>;

/* ────────────────────────────────────────────────────────────────────────
 * Broadcast messages (product updates / announcements shown in the widget)
 * ──────────────────────────────────────────────────────────────────────── */

export type SupportBroadcastType =
  | 'update'
  | 'announcement'
  | 'maintenance'
  | 'marketing';

export type SupportBroadcastState =
  | 'draft'
  | 'scheduled'
  | 'published'
  | 'archived';

/** Who should see a broadcast. */
export type SupportBroadcastAudience =
  | { kind: 'all' }
  | { kind: 'products'; products: string[] }
  | { kind: 'restaurants'; restaurantIds: string[] };

export type SupportBroadcastMessageDocument = WithAudit<{
  id: string;
  title: Translation<string>;
  body: Translation<string>;
  type: SupportBroadcastType;
  state: SupportBroadcastState;
  audience: SupportBroadcastAudience;
  publishedAt?: number;
  scheduledFor?: number;
  expiresAt?: number | null;
  cta?: { label: Translation<string>; url: string };
}>;

/* ────────────────────────────────────────────────────────────────────────
 * Conversation start templates (the "Bug melden" / "Funktionswunsch" buttons)
 * Config-driven so the set can be edited without code changes.
 * ──────────────────────────────────────────────────────────────────────── */

export type SupportTemplateFieldType =
  | 'text'
  | 'textarea'
  | 'select'
  | 'multiple_choice'
  | 'screenshot';

export interface SupportTemplateField {
  id: string;
  label: Translation<string>;
  type: SupportTemplateFieldType;
  required?: boolean;
  placeholder?: Translation<string>;
  options?: { id: string; label: Translation<string> }[];
}

export interface SupportConversationTemplate {
  /** Stable id, e.g. 'bug' | 'feature' | 'question' | 'billing'. */
  id: string;
  label: Translation<string>;
  description?: Translation<string>;
  icon?: string;
  /** If the template should open a ticket, of which type. */
  ticketType?: SupportTicketType;
  /** Default owning team. */
  group?: SupportTeam;
  severityDefault?: number;
  /** First (auto) message shown when the template is opened. */
  intro: Translation<string>;
  /** Structured intake collected before the AI takes over. */
  fields: SupportTemplateField[];
}

/* ────────────────────────────────────────────────────────────────────────
 * AI agent context (passed to ElevenLabs as dynamic variables)
 * ──────────────────────────────────────────────────────────────────────── */

export interface SupportAiContext {
  conversationId: string;
  restaurantId: string;
  restaurantName?: string;
  userId: string;
  userName?: string;
  email?: string;
  language?: string;
  role?: string;
  products?: string[];
  channel: SupportChannel;
  templateId?: string;
}
